import { useParams, useSearch } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { ApiClientsProvider } from '@/api/api-clients-context'
import { FhVitalsClientProvider } from '@/sdk/fh-vitals-client-context'
import { MindsetClientProvider } from '@/sdk/mindset-client-context'
import { ShenAiClientProvider } from '@/sdk/shenai-client-context'
import { parseScanCode } from '@/lib/link'
import { loadRuntimeConfig } from '@/lib/runtime-config'
import { thisPage, useStallWatchdog } from '@/lib/stall-recovery'
import { I18nProvider } from '@/i18n/i18n-context'
import { ScanFlow } from '@/flow/scan-flow'
import { CouldNotLoadScreen } from '@/flow/screens/could-not-load-screen'
import { LoadingScreen } from '@/flow/screens/loading-screen'
import { NotFoundPage } from './not-found-page'

/**
 * The scan page reached by the link this service mints: `{WebappBaseUrl}/{code}`, where the
 * credential is the WHOLE path (docs/decisions.md D-41).
 *
 * Two things happen here and nowhere else. The path segment is tested for the shape of a grant code
 * (`parseScanCode`), and a segment that is not one renders the not-found page INSTEAD of the flow.
 * That branch is the difference between "this scan link is dead" and "this was never a link": a
 * crawler asking for `/robots.txt`, a browser asking for `/favicon.ico` on its own, or a patient who
 * mistyped the host part of the URL must not be told their scan is broken, must not cost a challenge
 * against Auth, and must not meet a blank page - which is what handing an arbitrary path to
 * ScanFlow would produce once the challenge came back 410 for something that was never a grant.
 *
 * A code-SHAPED path that no grant answers to still reaches the flow and still ends at the dead-link
 * screen, and that is deliberate: a patient who typed an O where their code has a zero has made a
 * transcription slip on a real link, and D-14 is explicit that such a slip should meet a failed
 * lookup rather than a closed door.
 */
export function ScanCodePage() {
  const { code } = useParams({ from: '/$code' })
  const credential = parseScanCode(code)

  if (credential === null) {
    return <NotFoundPage />
  }

  return <ScanPage token={credential} />
}

/**
 * The scan page reached by an OLD link: `{WebappBaseUrl}/v2/scan?token={credential}`.
 *
 * It resolves the same flow rather than redirecting to the new shape, and the reason is not
 * tidiness. The old query parameter can carry the pre-D-14 signed token,
 * `base64url(grantId).base64url(signature)`, which contains a `.` - and a `.` is exactly what
 * `parseScanCode` refuses, because it is what separates a credential from every file this origin
 * serves. Redirecting `?token=abc.def` to `/abc.def` would turn a working link into "Page not
 * found". So the two shapes are two readers of one flow, and only the new one is a path.
 */
export function LegacyScanPage() {
  const { token } = useSearch({ from: '/v2/scan' })

  return <ScanPage token={token} />
}

/**
 * Everything after the credential has been read, shared by both link shapes: fetch the runtime
 * config, provide all three vendors' seams, run the flow.
 *
 * `token` is still `string | undefined` even though {@link ScanCodePage} can only hand it a string.
 * The legacy reader genuinely can produce undefined - `/v2/scan` with no query at all is a URL
 * somebody can arrive at - and the missing-token screen below is that patient's first real screen.
 */
function ScanPage({ token }: { token: string | undefined }) {
  const configQuery = useQuery({
    queryKey: ['runtime-config'],
    queryFn: () => loadRuntimeConfig(),
    staleTime: Infinity,
    retry: false,
  })

  /**
   * The first stage the stall watchdog covers, and the one outside the flow (docs/decisions.md D-46).
   *
   * A `fetch` that never settles leaves this query pending for as long as the tab is open, and
   * `retry: false` means nothing else will ever disturb it.
   *
   * The screen it ends at is the flow's own, in English. Not because English is a default worth
   * defending, but because the language a patient reads is carried on the scan config and the fetch
   * that says where to ask for it is the one that stalled - and a patient with nothing to tap is the
   * fault this entry exists to remove, so the button matters more here than the translation that
   * cannot be had.
   */
  const exhausted = useStallWatchdog(token, configQuery.isPending ? 'runtime-config' : null)

  if (!token) {
    return (
      <Centered>
        <p className="text-sm text-slate-600">This link is missing its token.</p>
      </Centered>
    )
  }

  if (exhausted) {
    return (
      <Centered>
        <I18nProvider language="en">
          <CouldNotLoadScreen onRetry={() => thisPage.reload()} />
        </I18nProvider>
      </Centered>
    )
  }

  if (configQuery.isError) {
    return (
      <Centered>
        <p className="text-sm text-slate-600">Something went wrong loading this page. Please try again.</p>
      </Centered>
    )
  }

  if (configQuery.isPending) {
    return (
      <Centered>
        <LoadingScreen />
      </Centered>
    )
  }

  return (
    <ApiClientsProvider config={configQuery.data}>
      {/* All three vendors' seams are provided; which one is used is the scan config's answer, and
          no SDK is loaded until it says so (docs/decisions.md D-11, D-32). */}
      <FhVitalsClientProvider>
        <ShenAiClientProvider>
          <MindsetClientProvider>
            <ScanFlow token={token} runtimeConfig={configQuery.data} />
          </MindsetClientProvider>
        </ShenAiClientProvider>
      </FhVitalsClientProvider>
    </ApiClientsProvider>
  )
}

function Centered({ children }: { children: ReactNode }) {
  return <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">{children}</div>
}
